import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import "highlight.js/styles/github-dark.css";
import { Components } from "react-markdown";
import { getSessionValidity, sendThumbsFeedback } from "../../../Rest_api/RestApi";
import { redirectToLogin } from "../../Protected";
import AgentChart, { DataPoint, PlotParameters } from "../../../components/agent/AgentChart";
import { AgentQuestions } from "./AgentQuestions";
import { SquarePen, Mic, MicOff, ThumbsUp, ThumbsDown, Check, MessageSquare, Plus, Clock, Menu, X, ChevronLeft, ChevronRight, GripVertical } from "lucide-react";
import {
  LogoIcon,
  LoadingSpinner,
  SendIcon,
  ChevronDownIcon,
  DownloadIcon,
} from "../../../components/agent/AgentIcons";
import { markdownStyles} from "../../../components/agent/AgentStyles";
import { ReactMic } from "react-mic";
import { AgentEvent } from "../../../models/agent";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AGENT_API_BASE, BASE_URL } from "../../../configs";
import AgentTable from "../../../components/agent/AgentTable";

/**
 * Chat page that matches the supplied mock‑up and streams responses from the backend.
 * Technologies: React + TypeScript + TailwindCSS.
 *
 * – Creates a session on first mount and keeps its id in a ref.
 * – `sendMessage` POSTs to `/run_sse` and decodes the Server‑Sent‑Events stream.
 * – Replies from authors in MAIN_AUTHORS appear inline; other authors collapse under a <details> "Thinking …" control.
 */

interface ApiResponse {
  status: boolean;
  message?: string;
  data?: any;
}

type Author = "user" | string;

interface Message {
  id: string;
  author: Author;
  content: string;
  chartData?: DataPoint[];
  tableData?: any[];
  chartParameters?: PlotParameters;
  invocation_id?: string;
  functionCall?: boolean;
  isStreaming?: boolean; // Track if message is still receiving partial updates
  responseId?: string; // Track which response/session this message belongs to
}

interface QuestionType {
  heading?: string;
  text: string;
  highlights?: string[];
}

interface ChartData {
  date: string;
  mwh: string;
  legend: string;
}


// Update these lists to tweak visibility rules without touching render code.
const MAIN_AUTHORS: Author[] = ["DataShowAgent", "gnai_ruvitl"];
const THINKING_AUTHORS: Author[] = [
  "PlotRequirementCreator",
  "PlotMetadata",
  "data_fetching_agent",
  "data_requirement_creator",
  "data_validation_agent",
  "plot_data_extractor",
  "SchemaExtractor",
  "PlotDataExtractor",
  "Checker",
]; 

const PRESET_QUESTIONS: string[] = [];
const API_BASE = AGENT_API_BASE;
// const API_BASE = "http://127.0.0.1:8001";
const APP_NAME = "collective";
const usertoken =   localStorage.getItem("token");

// Add these interfaces after the existing interfaces
interface RecordingState {
  isRecording: boolean;
  recognition: SpeechRecognition | null;
  duration: number;
  isLongPress: boolean;
}

// Add Web Speech API type definitions
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
  interpretation: any;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionError extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onaudioend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onaudiostart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror:
    | ((this: SpeechRecognition, ev: SpeechRecognitionError) => any)
    | null;
  onnomatch: ((this: SpeechRecognition, ev: Event) => any) | null;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any)
    | null;
  onsoundend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onsoundstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onspeechstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
  prototype: SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

const ChartPlaceholder = ({
  data,
  parameters,
}: {
  data: DataPoint[];
  parameters: PlotParameters | null;
}) => {
  return <AgentChart data={data} chartPar={parameters} /> ;
};

// Add this placeholder PlusIcon at the top of the file or use an existing one
const PlusIcon = () => (
  <svg
    width="24"
    height="24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
    />
    <line
      x1="12"
      y1="8"
      x2="12"
      y2="16"
      stroke="currentColor"
      strokeWidth="2"
    />
    <line
      x1="8"
      y1="12"
      x2="16"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const SquareStopIcon = ({ className = "" }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <rect x="4" y="4" width="12" height="12" rx="3" fill="currentColor" />
  </svg>
);

// Helper to extract message text from content
function getMessageText(content: any): string {
  if (typeof content === "string") return content;
  if (content && typeof content === "object" && content.text) return content.text;
  
  // Handle function call events
  if (content && typeof content === "object" && content.parts && Array.isArray(content.parts)) {
    const functionCall = content.parts.find((part: any) => part.functionCall);
    if (functionCall && functionCall.functionCall) {
      const funcName = functionCall.functionCall.name;
      const args = functionCall.functionCall.args;
      
      if (funcName === "code_executor_plot" && args && args.query) {
        return `Executing query: ${args.query.substring(0, 100)}${args.query.length > 100 ? '...' : ''}`;
      }
      else if(funcName === "transfer_to_agent" && args && args.agent_name){
        return "Transferring to "+args.agent_name;
      }
      
      return `Executing ${funcName}`;
    }
    
    // Handle regular parts
    return content.parts.map((p: any) => p.text || JSON.stringify(p)).join(" ");
  }
  
  return JSON.stringify(content);
}

// Helper function to convert agent names to user-friendly names
function getAgentDisplayName(agentName: string, content?: any): string {
  // Special handling for PlotDataExtractor with function calls
  if (agentName === "PlotDataExtractor" && content && typeof content === "object") {
    if (content.parts && Array.isArray(content.parts)) {
      const hasFunctionCall = content.parts.some((part: any) => part.functionCall);
      if (hasFunctionCall) {
        return "Executing Query";
      }
    }
  }
  
  switch (agentName) {
    case "PlotRequirementCreator":
      return "Creating Plot Requirements";
    case "PlotMetadata":
      return "Writing Chart Titles and Labels";
    case "data_fetching_agent":
      return "Fetching Data";
    case "data_requirement_creator":
      return "Creating Data Requirements";
    case "data_validation_agent":
      return "Validating Data";
    case "plot_data_extractor":
      return "Extracting Plot Data";
    case "data_processing_agent":
      return "Processing Data";
    case "data_analysis_agent":
      return "Analyzing Data";
    case "data_visualization_agent":
      return "Creating Visualization";
    case "data_insights_agent":
      return "Generating Insights";
    case "data_reporting_agent":
      return "Preparing Report";
    case "data_qa_agent":
      return "Quality Assurance";
    case "SchemaExtractor":
      return "Extracting Schema";
    case "PlotDataExtractor":
      return "Extracting Plot Data";
    case "Checker":
      return "Checking Data";
    default:
      return "Thinking";
  }
}

interface Session {
  id: string;
}

interface EventResponse {
  events: AgentEvent[];
}

// Extract InputBox as a separate stateless component
interface InputBoxProps {
  input: string;
  setInput: (value: string) => void;
  sendMessage: (text: string) => void;
  isLoading: boolean;
  isSessionLoading: boolean;
  isSessionCreating: boolean;
  isTranscribing: boolean;
  recordingState: RecordingState;
  handleMicPress: () => void;
  handleMicRelease: () => void;
}

const InputBox: React.FC<InputBoxProps> = ({
  input,
  setInput,
  sendMessage,
  isLoading,
  isSessionLoading,
  isSessionCreating,
  isTranscribing,
  recordingState,
  handleMicPress,
  handleMicRelease,
}) => {
  return (
    <div className="p-3 rounded-[28px] bg-clip-padding contain-inline-size overflow-clip border border-gray-200 shadow-lg bg-white hover:shadow-xl transition-all duration-300 backdrop-blur-sm">
      <input
        className="w-full bg-transparent outline-none text-lg px-2 placeholder:text-gray-400 text-gray-800"
        placeholder="Message Vidyut AI..."
        value={input}
        style={{ fontFamily: "inherit" }}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (input.trim()) {
              sendMessage(input);
            }
          }
        }}
        disabled={isSessionLoading || isLoading || isSessionCreating}
      />
      <div className="flex justify-end gap-2 mt-1">
        <button
          className={`rounded-full p-3 flex items-center justify-center transition-all duration-200 ${
            recordingState.isRecording
              ? "bg-red-100 hover:bg-red-200 text-red-600 scale-110"
              : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:scale-105"
          }`}
          onMouseDown={handleMicPress}
          onMouseUp={handleMicRelease}
          onMouseLeave={handleMicRelease}
          onTouchStart={handleMicPress}
          onTouchEnd={handleMicRelease}
          title="Tap to start/stop recording, hold for quick record"
        >
          {isTranscribing ? (
            <LoadingSpinner />
          ) : recordingState.isRecording ? (
            <SquareStopIcon className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </button>
        <button
          className={`rounded-full text-white p-3 flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg ${
            isLoading || !input.trim()
              ? "bg-[#f1935c]/60 cursor-not-allowed"
              : "bg-gradient-to-r from-[#f1935c] to-[#e67f45] hover:from-[#e67f45] hover:to-[#d46a2f] hover:scale-105 active:scale-95"
          }`}
          onClick={() => {
            if (input.trim()) {
              sendMessage(input);
            }
          }}
          disabled={isLoading || !input.trim()}
          style={{ fontFamily: "inherit" }}
        >
          {isLoading ? <LoadingSpinner /> : <SendIcon />}
        </button>
      </div>
    </div>
  );
};

export default function Agent() {
  /** Chat state */
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [hasMessages, setHasMessages] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionCreating, setIsSessionCreating] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [currentPresetIndex, setCurrentPresetIndex] = useState(0);
  const [ratedMessages, setRatedMessages] = useState<Record<string, { isUp: boolean; isLoading: boolean }>>({});
  // Track loading state per response
  const [activeResponseId, setActiveResponseId] = useState<string | null>(null);
  const [completedResponseIds, setCompletedResponseIds] = useState<Set<string>>(new Set());
  const sessionIdRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const pendingPlotParamsRef = useRef<PlotParameters | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    recognition: null,
    duration: 0,
    isLongPress: false,
  });
  const [speechError, setSpeechError] = useState<string | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pressStartTimeRef = useRef<number>(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const [isRetrying, setIsRetrying] = useState(false);
  const [isBrowserSupported, setIsBrowserSupported] = useState<boolean | null>(
    null
  );
  // New state for sidebar
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionIdFromUrl = searchParams.get("session");
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [sessionLoadError, setSessionLoadError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 256; // Default 256px (w-64)
  });
  const [isResizing, setIsResizing] = useState(false);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);
  
  // Chat history from API
  const [chatHistory, setChatHistory] = useState<Array<{ id: string; title: string; timestamp: Date; preview: string }>>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Tab questions
  const TAB_QUESTIONS: Record<
    string,
    Array<{ heading: string; questions: QuestionType[] }>
  > = AgentQuestions;

  // Function to run preset questions in sequence
  const runPresetQuestions = async () => {
    if (currentPresetIndex < PRESET_QUESTIONS.length) {
      const question = PRESET_QUESTIONS[currentPresetIndex];
      await sendMessage(question);
      setCurrentPresetIndex((prev) => prev + 1);
    }
  };

  // Run preset questions when messages change and current message is complete
  useEffect(() => {
    if (
      messages.length > 0 &&
      !isLoading &&
      currentPresetIndex < PRESET_QUESTIONS.length
    ) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.author !== "user") {
        // Wait a bit before sending next question
        const timer = setTimeout(runPresetQuestions, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [messages, isLoading]);

  /** Auto‑scroll on new messages */
  useEffect(() => {
    const scrollToBottom = () => {
      if (listRef.current) {
        // Force scroll to bottom immediately when new messages arrive
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    };

    // Call immediately
    scrollToBottom();

    // Also call after a short delay to handle dynamic content
    const timeoutId = setTimeout(scrollToBottom, 100);

    return () => clearTimeout(timeoutId);
  }, [messages]);

  /** Check session validity and create session on mount */
  // useEffect(() => {
  //   // First check session validity
  //   getSessionValidity().then((res: ApiResponse) => {
  //     if (res.status === false) {
  //       redirectToLogin(window.location.href);
  //       return;
  //     }

  //     // If session is valid, create a new chat session
  //     console.log("Creating session...");
  //     fetch(`${API_BASE}/apps/${APP_NAME}/users/${usertoken}/sessions`, {
  //       method: "POST",
  //     })
  //       .then((res) => res.json())
  //       .then((data: any) => {
  //         console.log("Session created:", data);
  //         if (data["id"]) {
  //           sessionIdRef.current = data["id"];
  //           console.log("Session ID set:", sessionIdRef.current);
  //           // Start running preset questions after session is created
  //           runPresetQuestions();
  //         }
  //       })
  //       .catch((error) => {
  //         console.error("Error creating session:", error);
  //       });
  //   });
  // }, []);

  useEffect(() => {
    // Add the styles to the document head
    const styleElement = document.createElement("style");
    styleElement.innerHTML = markdownStyles + `
      @keyframes fade-in {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes slide-in-left {
        from {
          opacity: 0;
          transform: translateX(-20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes slide-in-right {
        from {
          opacity: 0;
          transform: translateX(20px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes shake {
        0%, 100% { transform: translateX(0); }
        10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
        20%, 40%, 60%, 80% { transform: translateX(5px); }
      }
      @keyframes bounce-slow {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      .animate-fade-in {
        animation: fade-in 0.4s ease-out;
      }
      .animate-slide-in-left {
        animation: slide-in-left 0.4s ease-out;
      }
      .animate-slide-in-right {
        animation: slide-in-right 0.4s ease-out;
      }
      .animate-shake {
        animation: shake 0.5s ease-in-out;
      }
      .animate-bounce-slow {
        animation: bounce-slow 2s ease-in-out infinite;
      }
      .border-l-3 {
        border-left-width: 3px;
      }
      /* Custom scrollbar for sidebar */
      .sidebar-scroll::-webkit-scrollbar {
        width: 6px;
      }
      .sidebar-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .sidebar-scroll::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
      }
      .sidebar-scroll::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Add this useEffect to check browser support on component mount
  useEffect(() => {
    const checkBrowserSupport = () => {
      const isSupported = !!(
        window.SpeechRecognition || window.webkitSpeechRecognition
      );
      setIsBrowserSupported(isSupported);
      if (!isSupported) {
        setSpeechError(
          "Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari."
        );
      }
    };
    checkBrowserSupport();
  }, []);

  // New Chat handler
  const handleNewChat = () => {
    setMessages([]);
    setInput("");
    setHasMessages(false);
    setSessionLoadError(null);
    sessionIdRef.current = null;
    setSelectedSession(null);
    pendingPlotParamsRef.current = null; // Clear pending plot parameters
    setActiveResponseId(null); // Clear active response
    setCompletedResponseIds(new Set()); // Clear completed responses
    // Remove session param from URL
    const params = new URLSearchParams(window.location.search);
    params.delete("session");
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Scroll to bottom helper
  const scrollToBottom = () => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  };

  // useEffect for sessionIdFromUrl

  // Scroll to bottom on new message
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /** Send a user message and stream the response */
  const sendMessage = async (text: string) => {
    setSessionError(null); // Clear previous error
    if (!text.trim()) {
      return;
    }
    let isNewSession = false;
    // If no session, create one first
    if (!sessionIdRef.current) {
      isNewSession = true;
      setIsSessionCreating(true);
      try {
        const res = await getSessionValidity();
        if (res.status === false) {
          redirectToLogin(window.location.href);
          setIsSessionCreating(false);
          return;
        }
        // Create a new chat session
        const data = await fetch(`${API_BASE}/apps/${APP_NAME}/users/${usertoken}/sessions`, {
          method: "POST",
        }).then((res) => res.json());
        if (data && data["id"]) {
          sessionIdRef.current = data["id"];
          // Update the URL with the new session id using navigate to trigger React Router updates
          const params = new URLSearchParams(window.location.search);
          params.set("session", sessionIdRef.current || "");
          navigate(`?${params.toString()}`, { replace: true });
          // Add new session to chat history immediately without full refresh
          if (sessionIdRef.current) {
            const newSession = {
              id: sessionIdRef.current,
              title: 'New Conversation',
              timestamp: new Date(),
              preview: '',
            };
            setChatHistory(prev => [newSession, ...prev]);
          }
        } else {
          setSessionError("Could not start chat, please try again later.");
          setIsSessionCreating(false);
          return;
        }
      } catch (error) {
        setSessionError("Could not start chat, please try again later.");
        setIsSessionCreating(false);
        return;
      }
      setIsSessionCreating(false);
    }
    setIsLoading(true);
    setHasMessages(true);
    // Clear pending plot parameters when starting a new conversation
    pendingPlotParamsRef.current = null;
    
    // Generate a new response ID for this request
    const currentResponseId = crypto.randomUUID();
    setActiveResponseId(currentResponseId);
    
    if (isNewSession) {
      setMessages([{ id: crypto.randomUUID(), author: "user", content: text, responseId: currentResponseId }]);
    } else {
      setMessages((m) => [
        ...m,
        { id: crypto.randomUUID(), author: "user", content: text, responseId: currentResponseId },
      ]);
    }
    setInput("");
    try {
      const resp = await fetch(`${API_BASE}/run_sse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify({
          app_name: APP_NAME,
          usertoken: usertoken,
          session_id: sessionIdRef.current, // Always use the active session id
          new_message: { role: "user", parts: [{ text }] },
          streaming: true,
        }),
      });
      if (!resp.body) {
        setIsLoading(false);
        return;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          setIsLoading(false);
          // Clear pending plot parameters when streaming is complete
          pendingPlotParamsRef.current = null;
          // Mark this response as completed
          if (currentResponseId) {
            setCompletedResponseIds(prev => {
              const newSet = new Set(prev);
              newSet.add(currentResponseId);
              return newSet;
            });
            setActiveResponseId(null);
          }
          // Mark all messages for this response as no longer streaming
          setMessages((m) => m.map(msg => 
            msg.responseId === currentResponseId 
              ? { ...msg, isStreaming: false }
              : msg
          ));
          // Update the title of the new session after first message completes
          if (isNewSession && sessionIdRef.current && usertoken) {
            // First, update with the message text immediately (optimistic update)
            const messageText = text.trim();
            if (messageText) {
              const truncatedTitle = messageText.length > 40 
                ? messageText.substring(0, 40) + '...' 
                : messageText;
              setChatHistory(prev => prev.map(chat => 
                chat.id === sessionIdRef.current 
                  ? { ...chat, title: truncatedTitle }
                  : chat
              ));
            }
            
            // Then fetch the actual title from backend after a delay (in case backend processes it differently)
            setTimeout(() => {
              fetch(`${BASE_URL}/gpt/sessions/?app_name=${APP_NAME}`, {
                method: 'GET',
                headers: {
                  'Authorization': usertoken,
                  'Content-Type': 'application/json',
                },
              })
                .then(res => res.json())
                .then(data => {
                  const sessions = Array.isArray(data) ? data : (data.results || []);
                  const updatedSession = sessions.find((s: any) => s.id === sessionIdRef.current);
                  if (updatedSession && updatedSession.title && updatedSession.title !== 'New Conversation') {
                    // Only update if backend has a different title
                    const truncatedTitle = updatedSession.title.length > 40 
                      ? updatedSession.title.substring(0, 40) + '...' 
                      : updatedSession.title;
                    setChatHistory(prev => prev.map(chat => 
                      chat.id === sessionIdRef.current 
                        ? { ...chat, title: truncatedTitle }
                        : chat
                    ));
                  }
                })
                .catch(err => console.error('Error updating session title:', err));
            }, 1000); // Small delay to ensure backend has processed the event
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const packets = buffer.split("\n\n");
        buffer = packets.pop()!;
        for (const p of packets) {
          if (!p.startsWith("data:")) continue;
          const jsonStr = p.replace(/^data:\s*/, "").trim();
          let evt: any;
          try {
            evt = JSON.parse(jsonStr);
          } catch (error) {
            continue;
          }
          
          const author: Author = evt.author || "model";
          const invocationId = evt.invocation_id || evt.invocationId;
          const isPartial = evt.partial === true;
          const plotParams = evt.actions?.stateDelta?.plot_metadata;
          const txt = (evt.content?.parts || [])
            .map((pt: any) => pt.text || "")
            .join("");

          // Handle chart data
          if (author === "chartData") {
            setMessages((m) => {
              const newMessage: Message = {
                id: invocationId || crypto.randomUUID(),
                author: "chart",
                content: "",
                chartData: Array.isArray(evt.content) ? evt.content : [evt.content],
                chartParameters: pendingPlotParamsRef.current || undefined,
                responseId: currentResponseId,
              };
              return [...m, newMessage];
            });
            continue;
          }
          
          // Handle table data
          if (author === "tableData") {
            setMessages((m) => {
              const newMessage: Message = {
                id: invocationId || crypto.randomUUID(),
                author: "table",
                content: "",
                tableData: evt.content,
                responseId: currentResponseId,
              };
              return [...m, newMessage];
            });
            continue;
          }

          // Handle plot parameters
          if (plotParams) {
            const params = plotParams as PlotParameters;
            pendingPlotParamsRef.current = params;
            
            // Update the most recent chart message with the new parameters
            setMessages((m) => {
              const chartMessages = m.filter(msg => msg.author === "chart");
              if (chartMessages.length > 0) {
                const lastChartMessage = chartMessages[chartMessages.length - 1];
                const lastChartIndex = m.findIndex(msg => msg.id === lastChartMessage.id);
                if (lastChartIndex !== -1) {
                  const updatedMessages = [...m];
                  updatedMessages[lastChartIndex] = {
                    ...lastChartMessage,
                    chartParameters: params
                  };
                  return updatedMessages;
                }
              }
              return m;
            });
            continue;
          }
          
          // Check if this is a function call event
          const hasFunctionCall = evt.content?.parts?.some((part: any) => part.functionCall);
          
          // Skip if no meaningful content
          if (!txt && !hasFunctionCall && !isPartial) continue;

          // Handle message streaming by invocation_id + author
          setMessages((m) => {
            // Find existing message by invocation_id and author
            const existingIdx = m.findIndex(
              msg => msg.invocation_id === invocationId && msg.author === author
            );
            
            if (isPartial) {
              // Partial event - append text chunk for streaming effect
              if (existingIdx >= 0) {
                // Append to existing message
                const updated = [...m];
                updated[existingIdx] = {
                  ...updated[existingIdx],
                  content: (typeof updated[existingIdx].content === 'string' 
                    ? updated[existingIdx].content 
                    : '') + txt,
                  isStreaming: true,
                };
                return updated;
              } else if (txt || hasFunctionCall) {
                // Create new message for streaming
                return [...m, {
                  id: crypto.randomUUID(),
                  author,
                  content: txt || evt.content,
                  invocation_id: invocationId,
                  functionCall: hasFunctionCall,
                  isStreaming: true,
                  responseId: currentResponseId,
                }];
              }
              return m;
            } else {
              // Final event (partial: false or undefined) - set definitive content
              if (existingIdx >= 0) {
                // Update existing with final content
                const updated = [...m];
                updated[existingIdx] = {
                  ...updated[existingIdx],
                  content: txt || updated[existingIdx].content,
                  functionCall: hasFunctionCall,
                  isStreaming: false,
                };
                
                // Check for thumbs_up in the event
                if (invocationId && evt.thumbs_up !== undefined) {
                  setRatedMessages(prev => ({
                    ...prev,
                    [invocationId]: {
                      isUp: evt.thumbs_up === true,
                      isLoading: false
                    }
                  }));
                }
                
                return updated;
              } else if (txt || hasFunctionCall) {
                // Create new message if we missed partial events
                const newMessage: Message = {
                  id: crypto.randomUUID(),
                  author,
                  content: txt || evt.content,
                  invocation_id: invocationId,
                  functionCall: hasFunctionCall,
                  isStreaming: false,
                  responseId: currentResponseId,
                };
                
                // Check for thumbs_up in the event
                if (invocationId && evt.thumbs_up !== undefined) {
                  setRatedMessages(prev => ({
                    ...prev,
                    [invocationId]: {
                      isUp: evt.thumbs_up === true,
                      isLoading: false
                    }
                  }));
                }
                
                return [...m, newMessage];
              }
              return m;
            }
          });
        }
      }
    } catch (error) {
      // Optionally set a message error here
      setIsLoading(false);
      // Clear pending plot parameters on error
      pendingPlotParamsRef.current = null;
      // Mark this response as completed (even on error)
      if (currentResponseId) {
        setCompletedResponseIds(prev => {
          const newSet = new Set(prev);
          newSet.add(currentResponseId);
          return newSet;
        });
        setActiveResponseId(null);
      }
      // Mark all messages for this response as no longer streaming
      setMessages((m) => m.map(msg => 
        msg.responseId === currentResponseId 
          ? { ...msg, isStreaming: false }
          : msg
      ));
    }
  };

  /** Helper for suggestion buttons */
  const quickAsk = (prompt: string) => sendMessage(prompt);

  const convertTableToCSV = (table: HTMLTableElement): string => {
    const rows = Array.from(table.querySelectorAll("tr"));
    return rows
      .map((row) => {
        const cells = Array.from(row.querySelectorAll("th, td"));
        return cells
          .map((cell) => {
            const text = cell.textContent?.trim() || "";
            // Escape quotes and wrap in quotes if contains comma
            return text.includes(",") ? `"${text.replace(/"/g, '""')}"` : text;
          })
          .join(",");
      })
      .join("\n");
  };

  const downloadTableAsCSV = (table: HTMLTableElement) => {
    const csv = convertTableToCSV(table);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "table_data.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Refresh session handler
  const refreshSession = () => {
    setMessages([]);
    setHasMessages(false);
    setCurrentPresetIndex(0);
    sessionIdRef.current = null;
    pendingPlotParamsRef.current = null; // Clear pending plot parameters
    getSessionValidity().then((res: ApiResponse) => {
      if (res.status === false) {
        redirectToLogin(window.location.pathname);
        return;
      }
      fetch(`${API_BASE}/apps/${APP_NAME}/users/${usertoken}/sessions`, {
        method: "POST",
      })
        .then((res) => res.json())
        .then((data: any) => {
          if (data["id"]) {
            sessionIdRef.current = data["id"];
            runPresetQuestions();
          }
        })
        .catch((error) => {
          console.error("Error creating session:", error);
        });
    });
  };

  // Add this function after other functions
  const startRecording = async (isLongPress: boolean = false) => {
    try {
      // Reset retry count if starting fresh
      if (!recordingState.isRecording) {
        retryCountRef.current = 0;
      }

      // Check if browser supports speech recognition
      if (!isBrowserSupported) {
        setSpeechError(
          "Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari."
        );
        return;
      }

      setSpeechError(null); // Clear any previous errors
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US";

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        if (recordingState.isLongPress) {
          sendMessage(transcript);
        }
        // Reset retry count on successful recognition
        retryCountRef.current = 0;
        setIsRetrying(false);
      };

      recognition.onend = () => {
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
        }
        setRecordingState((prev) => ({
          ...prev,
          isRecording: false,
          recognition: null,
          duration: 0,
          isLongPress: false,
        }));
        setIsRetrying(false);
      };

      recognition.onerror = (event: SpeechRecognitionError) => {
        console.error("Speech recognition error:", event.error);

        if (event.error === "network" && retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          console.log(
            `Retrying speech recognition (attempt ${retryCountRef.current}/${MAX_RETRIES})`
          );
          setIsRetrying(true);
          // Wait a bit before retrying
          setTimeout(() => {
            if (recordingState.isRecording) {
              recognition.stop();
              startRecording(isLongPress);
            }
          }, 1000);
          return;
        }

        let errorMessage = "Speech recognition error occurred";

        switch (event.error) {
          case "network":
            errorMessage =
              "Network error. Please check your internet connection and try again.";
            break;
          case "not-allowed":
            errorMessage =
              "Microphone access was denied. Please allow microphone access.";
            break;
          case "aborted":
            errorMessage = "Speech recognition was aborted.";
            break;
          case "audio-capture":
            errorMessage =
              "No microphone was found. Please ensure a microphone is installed.";
            break;
          case "no-speech":
            errorMessage = "No speech was detected. Please try again.";
            break;
          case "service-not-allowed":
            errorMessage = "Speech recognition service is not allowed.";
            break;
        }

        setSpeechError(errorMessage);
        setRecordingState((prev) => ({
          ...prev,
          isRecording: false,
          recognition: null,
          duration: 0,
          isLongPress: false,
        }));
        // Reset retry count after max retries
        retryCountRef.current = 0;
        setIsRetrying(false);
      };

      recognition.start();
      setRecordingState((prev) => ({
        ...prev,
        isRecording: true,
        recognition,
        duration: 0,
        isLongPress,
      }));

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setRecordingState((prev) => ({
          ...prev,
          duration: prev.duration + 1,
        }));
      }, 1000);
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      setSpeechError("Failed to start speech recognition. Please try again.");
      retryCountRef.current = 0;
      setIsRetrying(false);
    }
  };

  const stopRecording = () => {
    if (recordingState.recognition && recordingState.isRecording) {
      recordingState.recognition.stop();
    }
  };

  const handleMicPress = () => {
    pressStartTimeRef.current = Date.now();
    longPressTimerRef.current = setTimeout(() => {
      // If we reach here, it's a long press
      startRecording(true);
    }, 200); // 200ms threshold for long press
  };

  const handleMicRelease = () => {
    const pressDuration = Date.now() - pressStartTimeRef.current;

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }

    if (pressDuration < 200) {
      // It's a tap
      if (recordingState.isRecording) {
        stopRecording();
      } else {
        startRecording(false);
      }
    } else if (recordingState.isRecording && recordingState.isLongPress) {
      // It's a long press release
      stopRecording();
    }
  };

  // Format duration to MM:SS
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleFeedback = async (invocationId: string, thumbsUp: boolean) => {
    try {
      // Set loading state for this message
      setRatedMessages(prev => ({
        ...prev,
        [invocationId]: { isUp: thumbsUp, isLoading: true }
      }));

      const result = await sendThumbsFeedback(invocationId, thumbsUp);
      if ('error' in result) {
        console.error("Failed to send feedback:", result.error);
        // Remove the rating on error
        setRatedMessages(prev => {
          const newState = { ...prev };
          delete newState[invocationId];
          return newState;
        });
      } else {
        // Keep the selected state but remove loading
        setRatedMessages(prev => ({
          ...prev,
          [invocationId]: { isUp: thumbsUp, isLoading: false }
        }));
      }
    } catch (error) {
      console.error("Error sending feedback:", error);
      // Remove the rating on error
      setRatedMessages(prev => {
        const newState = { ...prev };
        delete newState[invocationId];
        return newState;
      });
    }
  };

  // Handler to select session and update URL (do not set selectedSession directly)
  const handleSelectSession = (session: Session) => {
    const params = new URLSearchParams(window.location.search);
    params.set("session", session.id || "");
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Format timestamp for chat history
  const formatChatTimestamp = (timestamp: Date): string => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  // Fetch chat history from API
  const fetchChatHistory = async () => {
    if (!usertoken) return;
    
    setIsLoadingHistory(true);
    setHistoryError(null);
    
    try {
      const response = await fetch(`${BASE_URL}/gpt/sessions/?app_name=${APP_NAME}`, {
        method: 'GET',
        headers: {
          'Authorization': usertoken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch chat history');
      }

      const data = await response.json();
      
      // Handle paginated response (Django REST framework pagination)
      const sessions = Array.isArray(data) ? data : (data.results || []);
      
      // Transform API response to chat history format
      const transformedHistory = sessions.map((session: any) => {
        // Parse create_time from ISO string
        const timestamp = session.create_time 
          ? new Date(session.create_time) 
          : new Date();
        
        // Use title from API or default
        let title = session.title || 'New Conversation';
        
        // Truncate title if too long (for display)
        const maxTitleLength = 40;
        const displayTitle = title.length > maxTitleLength 
          ? title.substring(0, maxTitleLength) + '...' 
          : title;
        
        // Use title as preview (truncated if needed)
        const maxPreviewLength = 60;
        const preview = title.length > maxPreviewLength 
          ? title.substring(0, maxPreviewLength) + '...' 
          : title;
        
        return {
          id: session.id,
          title: displayTitle,
          timestamp: timestamp,
          preview: preview,
        };
      });
      
      setChatHistory(transformedHistory);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setHistoryError('Failed to load chat history');
      // Keep empty array on error
      setChatHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Fetch chat history on mount
  useEffect(() => {
    fetchChatHistory();
  }, []);

  // Load session events when a session is selected from URL
  useEffect(() => {
    // Abort controller to cancel previous requests
    const abortController = new AbortController();
    const currentSessionId = sessionIdFromUrl;
    
    if (currentSessionId && usertoken) {
      setIsSessionLoading(true);
      setSessionLoadError(null);
      
      fetch(`${BASE_URL}/gpt/sessions/${currentSessionId}/events/?app_name=${APP_NAME}`, {
        method: 'GET',
        headers: {
          'Authorization': usertoken,
          'Content-Type': 'application/json',
        },
        signal: abortController.signal,
      })
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to load session events');
          }
          return res.json();
        })
        .then(data => {
          // Check if this response is still relevant (user might have switched sessions)
          if (currentSessionId !== sessionIdFromUrl) {
            return; // Ignore stale response
          }
          
          const events = data.events || [];
          
          // Transform events into Message format
          const transformedMessages: Message[] = [];
          let currentResponseId = crypto.randomUUID();
          let pendingPlotParams: PlotParameters | null = null;
          
          events.forEach((event: any) => {
            const author: Author = event.author || "model";
            const invocationId = event.invocation_id || event.invocationId;
            const content = event.content || {};
            
            // Handle plot parameters (they might come before or after chart data)
            if (event.actions?.stateDelta?.plot_metadata) {
              const plotParams = event.actions.stateDelta.plot_metadata as PlotParameters;
              pendingPlotParams = plotParams;
              
              // If we already have chart messages, update the most recent one
              const chartMessages = transformedMessages.filter(msg => msg.author === "chart");
              if (chartMessages.length > 0) {
                const lastChartMessage = chartMessages[chartMessages.length - 1];
                const lastChartIndex = transformedMessages.findIndex(msg => msg.id === lastChartMessage.id);
                if (lastChartIndex !== -1) {
                  transformedMessages[lastChartIndex] = {
                    ...lastChartMessage,
                    chartParameters: plotParams
                  };
                }
              }
              return;
            }
            
            // Handle chart data
            if (author === "chartData") {
              transformedMessages.push({
                id: invocationId || crypto.randomUUID(),
                author: "chart",
                content: "",
                chartData: Array.isArray(content) ? content : [content],
                chartParameters: pendingPlotParams || undefined,
                responseId: currentResponseId,
              });
              // Keep pendingPlotParams in case plot_metadata comes after
              return;
            }
            
            // Handle table data
            if (author === "tableData") {
              transformedMessages.push({
                id: invocationId || crypto.randomUUID(),
                author: "table",
                content: "",
                tableData: content,
                responseId: currentResponseId,
              });
              return;
            }
            
            // Handle user messages
            if (author === "user") {
              const text = content.parts?.map((pt: any) => pt.text || "").join("") || "";
              transformedMessages.push({
                id: invocationId || crypto.randomUUID(),
                author: "user",
                content: text,
                invocation_id: invocationId,
                responseId: currentResponseId,
              });
              // Generate new response ID for next set of messages
              currentResponseId = crypto.randomUUID();
              return;
            }
            
            // Handle other messages (agents, etc.)
            const text = content.parts?.map((pt: any) => pt.text || "").join("") || "";
            const hasFunctionCall = content.parts?.some((part: any) => part.functionCall);
            
            if (text || hasFunctionCall) {
              transformedMessages.push({
                id: invocationId || crypto.randomUUID(),
                author: author,
                content: text || content,
                invocation_id: invocationId,
                functionCall: hasFunctionCall,
                isStreaming: false,
                responseId: currentResponseId,
              });
            }
          });
          
          // Double-check session is still the same before updating state
          if (currentSessionId === sessionIdFromUrl) {
            setMessages(transformedMessages);
            sessionIdRef.current = currentSessionId;
            setHasMessages(transformedMessages.length > 0);
            setIsSessionLoading(false);
          }
        })
        .catch(error => {
          // Ignore abort errors (they're expected when switching sessions)
          if (error.name === 'AbortError') {
            return;
          }
          // Only show error if this is still the current session
          if (currentSessionId === sessionIdFromUrl) {
            console.error('Error loading session events:', error);
            setSessionLoadError('Failed to load conversation');
            setIsSessionLoading(false);
          }
        });
    } else if (!sessionIdFromUrl) {
      // Clear messages when no session is selected
      setMessages([]);
      setHasMessages(false);
      sessionIdRef.current = null;
    }
    
    // Cleanup: abort the request if component unmounts or session changes
    return () => {
      abortController.abort();
    };
  }, [sessionIdFromUrl, usertoken]);

  // Handle sidebar resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = Math.min(Math.max(200, e.clientX), 500); // Min 200px, Max 500px
      setSidebarWidth(newWidth);
      localStorage.setItem('sidebarWidth', newWidth.toString());
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  return (
    <div className="w-full bg-gradient-to-br from-[#f6faf9] via-white to-[#f0f7f6] flex flex-row overflow-hidden" style={{ height: "100%" }}>
      {/* Sidebar */}
      <div 
        className={`${sidebarOpen ? (sidebarCollapsed ? 'w-16' : 'translate-x-0') : '-translate-x-full w-0'} transition-all duration-300 ease-in-out bg-white border-r border-gray-200 shadow-lg flex flex-col overflow-hidden fixed md:relative md:translate-x-0 z-50 h-full relative`}
        style={!sidebarCollapsed && sidebarOpen ? { width: `${sidebarWidth}px` } : {}}
      >
        {sidebarCollapsed ? (
          // Collapsed sidebar - minimal UI
          <div className="flex flex-col h-full items-center py-4 gap-4">
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
              aria-label="Expand sidebar"
              title="Expand sidebar"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                handleNewChat();
                setSidebarCollapsed(false);
              }}
              className="p-3 rounded-xl bg-gradient-to-r from-[#f1935c] to-[#e67f45] text-white shadow-md hover:shadow-lg hover:from-[#e67f45] hover:to-[#d46a2f] transition-all duration-200 hover:scale-105 active:scale-95"
              aria-label="New Chat"
              title="New Chat"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        ) : (
          // Expanded sidebar - full UI
          <>
            <div className="flex flex-col h-full">
              {/* Sidebar Header */}
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#f6faf9] to-white flex items-center justify-between gap-2">
                <h1 className="text-lg font-medium text-[#34656d] hidden md:block">Vidyut AI</h1>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setSidebarCollapsed(true)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                    aria-label="Collapse sidebar"
                    title="Collapse sidebar"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSidebarOpen(false)}
                    className="md:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
                    aria-label="Close sidebar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              {/* New Chat Button */}
              <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#f6faf9] to-white">
                <button
                  onClick={() => {
                    handleNewChat();
                    // Close sidebar on mobile after creating new chat
                    if (window.innerWidth < 768) {
                      setSidebarOpen(false);
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#f1935c] to-[#e67f45] text-white rounded-xl shadow-md hover:shadow-lg hover:from-[#e67f45] hover:to-[#d46a2f] transition-all duration-200 font-semibold text-sm hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Plus className="w-5 h-5" />
                  New Chat
                </button>
              </div>

              {/* Chat History */}
              <div className="flex-1 overflow-y-auto sidebar-scroll">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4 px-2">
                <MessageSquare className="w-4 h-4 text-[#34656d]" />
                <h2 className="text-sm font-medium text-[#34656d] uppercase tracking-wide">Chat History</h2>
              </div>
              
              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : historyError ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500 mb-2">{historyError}</p>
                  <button
                    onClick={fetchChatHistory}
                    className="text-xs text-[#34656d] hover:text-[#2d565d] underline"
                  >
                    Retry
                  </button>
                </div>
              ) : chatHistory.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No chat history yet</p>
                  <p className="text-xs text-gray-400 mt-1">Start a new conversation to see it here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {chatHistory.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => {
                        const params = new URLSearchParams(window.location.search);
                        params.set("session", chat.id);
                        navigate(`?${params.toString()}`, { replace: true });
                        // Close sidebar on mobile after selection
                        if (window.innerWidth < 768) {
                          setSidebarOpen(false);
                        }
                      }}
                      className={`w-full text-left p-3 rounded-lg transition-all duration-200 group ${
                        sessionIdFromUrl === chat.id
                          ? 'bg-gradient-to-r from-[#34656d]/10 to-[#2d565d]/10 border-2 border-[#34656d]/30 shadow-md'
                          : 'hover:bg-gray-50 border-2 border-transparent hover:border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-medium text-gray-800 truncate flex-1 group-hover:text-[#34656d] transition-colors">
                          {chat.title}
                        </h3>
                        <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          <span>{formatChatTimestamp(chat.timestamp)}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

              {/* Sidebar Footer */}
              <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span>AI Assistant Ready</span>
                </div>
              </div>
            </div>
            
            {/* Resize Handle */}
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                setIsResizing(true);
              }}
              className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-[#34656d]/20 transition-colors group"
              style={{ zIndex: 10 }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-gray-300 rounded-full group-hover:bg-[#34656d] transition-colors" />
            </div>
          </>
        )}
      </div>

      {/* Main chat area */}
      <div style={{ flex: 1, minWidth: 0 }} className="relative">
        {/* Sidebar Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md hover:shadow-lg border border-gray-200 text-[#34656d] hover:bg-gray-50 transition-all duration-200 hover:scale-105 active:scale-95 md:hidden"
          aria-label="Toggle sidebar"
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        
        {/* Overlay for mobile when sidebar is open */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/20 z-40 md:hidden transition-opacity duration-300"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {isSessionLoading ? (
          <div className="flex items-center justify-center h-full bg-gradient-to-br from-[#f6faf9] to-white">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="w-full bg-white flex flex-col overflow-hidden" style={{ height: "100%" }}>

            <div className="flex-1 flex flex-col items-center justify-center h-full w-full overflow-hidden">
              <div className="w-full h-full mx-auto bg-white shadow-2xl flex flex-col items-center overflow-y-hidden">
                {/* hero and quick actions - only show when no messages */}
                {!hasMessages && (
                  <main className="flex-1 flex flex-col items-center justify-center md:px-4 px-2 w-full">
                    <div className="w-full text-center max-w-2xl mx-auto animate-fade-in">
                      <p
                        className="text-xl md:text-2xl text-gray-600 font-medium mb-8"
                        style={{ fontFamily: "inherit" }}
                      >
                        How Can I Help You Today?
                      </p>
                      {/* Input box */}
                      <div className="w-full max-w-2xl mx-auto px-4">
                        {sessionError && (
                          <div className="text-red-600 text-center mb-4 font-medium bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-300 rounded-xl p-3 shadow-md animate-shake">
                            {sessionError}
                          </div>
                        )}
                        <InputBox
                          input={input}
                          setInput={setInput}
                          sendMessage={sendMessage}
                          isLoading={isLoading}
                          isSessionLoading={isSessionLoading}
                          isSessionCreating={isSessionCreating}
                          isTranscribing={isTranscribing}
                          recordingState={recordingState}
                          handleMicPress={handleMicPress}
                          handleMicRelease={handleMicRelease}
                        />       
                      </div>
                    </div>
                  </main>
                )}
                {/* Chat layout after first message - revert to previous style */}
                {hasMessages && (
                  <main className="flex-1 flex flex-col h-full items-center w-full">
                    {/* message list in scrollable area inside white container */}
                    <div
                      className="flex-1 w-full overflow-y-scroll h-full px-2 md:px-8 pt-3 md:pt-6"
                      style={{ scrollBehavior: "smooth" }}
                      ref={listRef}
                    >
                      <div className="max-w-6xl mx-auto w-full space-y-6 pb-4">
                        {(() => {
                          // Process messages in chronological order and group thinking with the most recent user message
                          const allMessages: JSX.Element[] = [];
                          let currentThinkingMessages: Message[] = [];
                          let lastUserMessageId: string | null = null;
                          
                          // Helper to check if any message in a group belongs to the active response
                          const isGroupStreaming = (msgs: Message[]) => 
                            msgs.some(m => m.responseId === activeResponseId && !completedResponseIds.has(m.responseId || ''));
                          
                          // Helper to check if a single message belongs to the active response
                          const isMessageStreaming = (msg: Message) => 
                            msg.responseId === activeResponseId && !completedResponseIds.has(msg.responseId || '');

                          // Process messages in order
                          messages.forEach((msg) => {
                            // If this is a user message, add it and any pending thinking messages
                            if (msg.author === "user") {
                              // Add any pending thinking messages from previous conversation
                              if (currentThinkingMessages.length > 0) {
                                const latestThinkingAgent = currentThinkingMessages[currentThinkingMessages.length - 1].author;
                                
                                const groupIsStreaming = isGroupStreaming(currentThinkingMessages);
                                const thinkingMsgsToRender = [...currentThinkingMessages];
                                allMessages.push(
                                  <div key={`thinking-group-${lastUserMessageId || 'previous'}`} className="w-full first:mt-2 animate-fade-in">
                                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                      <details className="group">
                                        <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200 hover:from-gray-100 hover:to-gray-50 transition-all duration-200">
                                          <div className="flex items-center gap-2 text-[#34656d]">
                                            {groupIsStreaming ? <LoadingSpinner /> : (
                                              <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                            <span className="font-semibold">
                                              {getAgentDisplayName(thinkingMsgsToRender[thinkingMsgsToRender.length - 1]?.author, thinkingMsgsToRender[thinkingMsgsToRender.length - 1]?.content)}
                                            </span>
                                          </div>
                                          <span className="text-sm text-gray-500 font-medium">
                                            ({thinkingMsgsToRender.length} steps)
                                          </span>
                                          <ChevronDownIcon />
                                        </summary>
                                        <div className="px-5 py-4 space-y-4 bg-gray-50/50">
                                          {thinkingMsgsToRender.map((thinkingMsg, idx) => (
                                            <div key={thinkingMsg.id} className="border-l-3 border-[#34656d] pl-5 py-2 bg-white/50 rounded-r-lg hover:bg-white transition-colors duration-200">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-semibold text-white bg-gradient-to-r from-[#34656d] to-[#2d565d] px-3 py-1 rounded-full shadow-sm">
                                                  Step {idx + 1}
                                                </span>
                                                <span className="text-xs text-gray-500 font-medium">
                                                  {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                                </span>
                                                <div className="ml-auto">
                                                  {isMessageStreaming(thinkingMsg) ? <LoadingSpinner /> : (
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                      </svg>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="text-sm text-gray-700 leading-relaxed">
                                                {getMessageText(thinkingMsg.content)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  </div>
                                );
                                currentThinkingMessages = []; // Clear after adding
                              }

                              // Add the user message
                              allMessages.push(
                                <div key={msg.id} className="w-full first:mt-2 animate-slide-in-right">
                                  <div className="flex justify-end">
                                    <div className="inline-block px-5 md:px-7 py-3 md:py-4 rounded-2xl shadow-lg max-w-[90%] md:max-w-[85%] bg-gradient-to-r from-[#f1935c] via-[#e67f45] to-[#f1935c] text-white backdrop-blur-sm border border-white/20">
                                      <div className="markdown-body" style={{ fontFamily: "inherit" }}>
                                        {getMessageText(msg.content)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                              lastUserMessageId = msg.id;
                            }

                            // Collect thinking messages
                            else if (THINKING_AUTHORS.includes(msg.author) || !(msg.author!=="PlotDataExtractor" && !msg.functionCall)) {
                              currentThinkingMessages.push(msg);
                            }
                            // Handle output messages (non-thinking, non-user, non-chart)
                            else if (msg.author !== "chart" && msg.author !== "table") {
                              // Add any pending thinking messages before the output
                              if (currentThinkingMessages.length > 0) {
                                const groupIsStreaming2 = isGroupStreaming(currentThinkingMessages);
                                const thinkingMsgsToRender2 = [...currentThinkingMessages];
                                
                                allMessages.push(
                                  <div key={`thinking-group-${lastUserMessageId || 'current'}`} className="w-full first:mt-2 animate-fade-in">
                                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                      <details className="group">
                                        <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200 hover:from-gray-100 hover:to-gray-50 transition-all duration-200">
                                          <div className="flex items-center gap-2 text-[#34656d]">
                                            {groupIsStreaming2 ? <LoadingSpinner /> : (
                                              <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                            <span className="font-semibold">
                                              {getAgentDisplayName(thinkingMsgsToRender2[thinkingMsgsToRender2.length - 1]?.author, thinkingMsgsToRender2[thinkingMsgsToRender2.length - 1]?.content)}
                                            </span>
                                          </div>
                                          <span className="text-sm text-gray-500 font-medium">
                                            ({thinkingMsgsToRender2.length} steps)
                                          </span>
                                          <ChevronDownIcon />
                                        </summary>
                                        <div className="px-5 py-4 space-y-4 bg-gray-50/50">
                                          {thinkingMsgsToRender2.map((thinkingMsg, idx) => (
                                            <div key={thinkingMsg.id} className="border-l-3 border-[#34656d] pl-5 py-2 bg-white/50 rounded-r-lg hover:bg-white transition-colors duration-200">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-semibold text-white bg-gradient-to-r from-[#34656d] to-[#2d565d] px-3 py-1 rounded-full shadow-sm">
                                                  Step {idx + 1}
                                                </span>
                                                <span className="text-xs text-gray-500 font-medium">
                                                  {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                                </span>
                                                <div className="ml-auto">
                                                  {isMessageStreaming(thinkingMsg) ? <LoadingSpinner /> : (
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                      </svg>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="text-sm text-gray-700 leading-relaxed">
                                                {getMessageText(thinkingMsg.content)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  </div>
                                );
                                currentThinkingMessages = []; // Clear after adding
                              }

                              // Add the output message
                              if(getMessageText(msg.content).includes("Transferring to") || getMessageText(msg.content).slice(0,9)==="Executing"){
                              }else{  
                              allMessages.push(
                                <div key={msg.id} className="w-full first:mt-2 animate-slide-in-left">
                                  <div className="flex justify-start">
                                    <div className="inline-block px-5 md:px-7 py-3 md:py-4 rounded-2xl shadow-xl max-w-[90%] md:max-w-[85%] bg-gradient-to-br from-[#34656d] via-[#2d565d] to-[#34656d] text-white backdrop-blur-sm border border-white/10">
                                      <div className="relative">
                                        <div className="markdown-body prose prose-sm max-w-none prose-invert" style={{ fontFamily: "inherit" }}>
                                          {TableMarkdown(msg)}
                                          {isMessageStreaming(msg) && (
                                            <span className="inline-block w-2 h-4 ml-1 bg-white/80 animate-pulse rounded-sm" />
                                          )}
                                        </div>
                                        {msg.invocation_id && !isMessageStreaming(msg) && (
                                          <div className="flex gap-2 mt-3 justify-end h-8">
                                            {ratedMessages[msg.invocation_id]?.isLoading ? (
                                              <div className="flex items-center justify-center w-9 h-9">
                                                <LoadingSpinner className="w-4 h-4" />
                                              </div>
                                            ) : (
                                              <>
                                                <button
                                                  onClick={() => handleFeedback(msg.invocation_id!, true)}
                                                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
                                                    ratedMessages[msg.invocation_id]?.isUp 
                                                      ? 'bg-white/25 shadow-md scale-110' 
                                                      : 'hover:bg-white/15 hover:scale-105'
                                                  }`}
                                                  title="Thumbs up"
                                                >
                                                  <ThumbsUp className={`w-4 h-4 transition-colors ${
                                                    ratedMessages[msg.invocation_id]?.isUp 
                                                      ? 'text-green-300' 
                                                      : 'text-white/70'
                                                  }`} />
                                                </button>
                                                <button
                                                  onClick={() => handleFeedback(msg.invocation_id!, false)}
                                                  className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
                                                    ratedMessages[msg.invocation_id]?.isUp === false 
                                                      ? 'bg-white/25 shadow-md scale-110' 
                                                      : 'hover:bg-white/15 hover:scale-105'
                                                  }`}
                                                  title="Thumbs down"
                                                >
                                                  <ThumbsDown className={`w-4 h-4 transition-colors ${
                                                    ratedMessages[msg.invocation_id]?.isUp === false 
                                                      ? 'text-red-300' 
                                                      : 'text-white/70'
                                                  }`} />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );}
                            }
                            // Handle chart messages
                            else if (msg.author === "chart") {
                              // Add any pending thinking messages before the chart
                              if (currentThinkingMessages.length > 0) {
                                const groupIsStreaming3 = isGroupStreaming(currentThinkingMessages);
                                const thinkingMsgsToRender3 = [...currentThinkingMessages];
                                
                                allMessages.push(
                                  <div key={`thinking-group-${lastUserMessageId || 'before-chart'}`} className="w-full first:mt-2 animate-fade-in">
                                    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                      <details className="group">
                                        <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200 hover:from-gray-100 hover:to-gray-50 transition-all duration-200">
                                          <div className="flex items-center gap-2 text-[#34656d]">
                                            {groupIsStreaming3 ? <LoadingSpinner /> : (
                                              <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              </div>
                                            )}
                                            <span className="font-semibold">
                                              {getAgentDisplayName(thinkingMsgsToRender3[thinkingMsgsToRender3.length - 1]?.author, thinkingMsgsToRender3[thinkingMsgsToRender3.length - 1]?.content)}
                                            </span>
                                          </div>
                                          <span className="text-sm text-gray-500 font-medium">
                                            ({thinkingMsgsToRender3.length} steps)
                                          </span>
                                          <ChevronDownIcon />
                                        </summary>
                                        <div className="px-5 py-4 space-y-4 bg-gray-50/50">
                                          {thinkingMsgsToRender3.map((thinkingMsg, idx) => (
                                            <div key={thinkingMsg.id} className="border-l-3 border-[#34656d] pl-5 py-2 bg-white/50 rounded-r-lg hover:bg-white transition-colors duration-200">
                                              <div className="flex items-center gap-2 mb-2">
                                                <span className="text-xs font-semibold text-white bg-gradient-to-r from-[#34656d] to-[#2d565d] px-3 py-1 rounded-full shadow-sm">
                                                  Step {idx + 1}
                                                </span>
                                                <span className="text-xs text-gray-500 font-medium">
                                                  {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                                </span>
                                                <div className="ml-auto">
                                                  {isMessageStreaming(thinkingMsg) ? <LoadingSpinner /> : (
                                                    <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                      </svg>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="text-sm text-gray-700 leading-relaxed">
                                                {getMessageText(thinkingMsg.content)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </details>
                                    </div>
                                  </div>
                                );
                                currentThinkingMessages = []; // Clear after adding
                              }
                              allMessages.push(
                                <ChartPlaceholder
                                  key={msg.id}
                                  data={msg.chartData!}
                                  parameters={msg.chartParameters || null}
                                />
                              );
                            }
                            else if (msg.author === "table") {
                              allMessages.push(
                                <AgentTable tableData={msg.tableData!} />
                               )}

                          });

                          // Add any remaining thinking messages at the end
                          if (currentThinkingMessages.length > 0) {
                            const groupIsStreamingFinal = isGroupStreaming(currentThinkingMessages);
                            const thinkingMsgsToRenderFinal = [...currentThinkingMessages];
                            
                            allMessages.push(
                              <div key={`thinking-group-final`} className="w-full first:mt-2 animate-fade-in">
                                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                  <details className="group" open={groupIsStreamingFinal}>
                                    <summary className="flex items-center gap-3 px-5 py-4 cursor-pointer bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200 hover:from-gray-100 hover:to-gray-50 transition-all duration-200">
                                      <div className="flex items-center gap-2 text-[#34656d]">
                                        {groupIsStreamingFinal ? <LoadingSpinner /> : (
                                          <div className="w-5 h-5 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                        )}
                                        <span className="font-semibold">
                                          {getAgentDisplayName(thinkingMsgsToRenderFinal[thinkingMsgsToRenderFinal.length - 1]?.author, thinkingMsgsToRenderFinal[thinkingMsgsToRenderFinal.length - 1]?.content)}
                                        </span>
                                      </div>
                                      <span className="text-sm text-gray-500 font-medium">
                                        ({thinkingMsgsToRenderFinal.length} steps)
                                      </span>
                                      <ChevronDownIcon />
                                    </summary>
                                    <div className="px-5 py-4 space-y-4 bg-gray-50/50">
                                      {thinkingMsgsToRenderFinal.map((thinkingMsg, idx) => (
                                        <div key={thinkingMsg.id} className="border-l-3 border-[#34656d] pl-5 py-2 bg-white/50 rounded-r-lg hover:bg-white transition-colors duration-200">
                                          <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs font-semibold text-white bg-gradient-to-r from-[#34656d] to-[#2d565d] px-3 py-1 rounded-full shadow-sm">
                                              Step {idx + 1}
                                            </span>
                                            <span className="text-xs text-gray-500 font-medium">
                                              {getAgentDisplayName(thinkingMsg.author, thinkingMsg.content)}
                                            </span>
                                            <div className="ml-auto">
                                              {thinkingMsg.isStreaming ? <LoadingSpinner /> : (
                                                <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center shadow-sm">
                                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                  </svg>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="text-sm text-gray-700 leading-relaxed">
                                            {getMessageText(thinkingMsg.content)}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </details>
                                </div>
                              </div>
                            );
                          }

                          return allMessages;
                        })()}
                      </div>
                    </div>
                    {/* Fixed chat composer at bottom of white container */}
                    <div className="bg-white/95 backdrop-blur-md w-full bottom-0 left-0 z-20 border-t border-gray-200 shadow-2xl">
                      <div className="p-4 max-w-2xl mx-auto sticky z-20">
                       <InputBox
                         input={input}
                         setInput={setInput}
                         sendMessage={sendMessage}
                         isLoading={isLoading}
                         isSessionLoading={isSessionLoading}
                         isSessionCreating={isSessionCreating}
                         isTranscribing={isTranscribing}
                         recordingState={recordingState}
                         handleMicPress={handleMicPress}
                         handleMicRelease={handleMicRelease}
                       />
                      </div>
                    </div>
                  </main>
                )}
              </div>
            </div>
            {/* Floating Beta Version badge */}
            <div className="fixed bottom-6 right-6 z-50 animate-bounce-slow">
              <span className="bg-gradient-to-r from-[#f1935c] to-[#e67f45] text-white px-5 py-2.5 rounded-full shadow-xl font-semibold text-sm tracking-wide border-2 border-white/30 backdrop-blur-sm">
                Beta Version
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

function TableMarkdown(msg: Message){

  return                                           <ReactMarkdown
  remarkPlugins={[remarkGfm]}
  rehypePlugins={[rehypeRaw, rehypeSanitize]}
  components={{
    // Custom components for markdown elements
    table: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableElement>) => (
      <div
        className="markdown-table-wrap"
        style={{
          position: "relative",
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "0.5rem",
          }}
        >
          <button
            className="table-download-btn flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#34656d] to-[#2d565d] text-white rounded-lg shadow-md hover:shadow-lg hover:from-[#2d565d] hover:to-[#34656d] transition-all duration-200 font-medium text-sm hover:scale-105 active:scale-95"
            style={{
              position: "static",
              zIndex: 10,
            }}
            onClick={(e) => {
              // Find the table element inside this container
              const container =
                e.currentTarget.closest(
                  ".markdown-table-wrap"
                );
              const table =
                container?.querySelector(
                  "table"
                );
              if (table)
                downloadTableAsCSV(
                  table as HTMLTableElement
                );
            }}
          >
            <DownloadIcon />
            Download CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table
            className="min-w-full"
            {...props}
          >
            {children}
          </table>
        </div>
      </div>
    ),
    thead: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableSectionElement>) => (
      <thead {...props}>{children}</thead>
    ),
    th: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableHeaderCellElement>) => (
      <th
        className="px-4 py-3 text-left text-sm font-semibold text-white/90"
        {...props}
      >
        {children}
      </th>
    ),
    td: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableDataCellElement>) => (
      <td
        className="px-4 py-3 text-sm whitespace-normal text-white/80"
        {...props}
      >
        {children}
      </td>
    ),
    tr: ({
      children,
      ...props
    }: React.HTMLProps<HTMLTableRowElement>) => (
      <tr
        className="transition-colors duration-150"
        {...props}
      >
        {children}
      </tr>
    ),
    code: ({
      inline,
      className,
      children,
      ...props
    }: any) => {
      return inline ? (
        <code
          className="px-1.5 py-0.5 rounded-md bg-gray-700/50 text-gray-100 text-sm"
          {...props}
        >
          {children}
        </code>
      ) : (
        <div className="rounded-md bg-gray-800/50 p-4">
          <code
            className="text-gray-100 text-sm"
            {...props}
          >
            {children}
          </code>
        </div>
      );
    },
    p: ({ children }) => (
      <p className="markdown-paragraph">
        {children}
      </p>
    ),
  }}
>
  {getMessageText(msg.content)}
</ReactMarkdown>
}

}
